import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import Button from 'components/base/Button';
import SearchBox from 'components/common/SearchBox';
import ClaimRecordsDailyChart from 'components/charts/e-charts/ClaimRecordsDailyChart';
import RewardClaimHistoryTable, {
  tableColumns
} from 'components/tables/RewardClaimHistoryTable';
import { errorHandler } from 'helpers/response-utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { ChangeEvent, useEffect, useState } from 'react';
import { Card, Col, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface DailyStat {
  date: string;
  count: number;
}

const RewardClaimTransactionList = () => {
  const nav = useNavigate();
  const [rewardClaims, setRewardClaims] = useState([]);
  const token = localStorage.getItem('token');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [totalRecords, setTotalRecords] = useState(0);
  const [dailyStats, setDailyStats] = useState<DailyStat[]>([]);
  const [chartLoading, setChartLoading] = useState(true);

  useEffect(() => {
    getDailyStats();
  }, []);

  useEffect(() => {
    getList();
  }, [statusFilter, pagination]);

  const getDailyStats = async () => {
    try {
      setChartLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/transaction/reward-claim/daily-stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (response.data['status'] === 'success') {
        setDailyStats(response.data['data']['dailyStats']);
      }
    } catch (err) {
      console.error('Failed to fetch daily stats', err);
    } finally {
      setChartLoading(false);
    }
  };

  const getList = async () => {
    const params: Record<string, any> = {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize
    };
    const response = await axios.get(
      `${process.env.REACT_APP_ENDPOINT}/transaction/reward-claim`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: params
      }
    );
    errorHandler(response);
    if (response.data['status'] == 'error') {
      toast.error(response.data['message']);
      return false;
    }
    setRewardClaims(response.data['data']['rewardClaims']);
    setTotalRecords(response.data['data']['totalRecords']);
  };
  const handlePaginationChange = (updater: any) => {
    const newState =
      typeof updater === 'function' ? updater(pagination) : updater;
    setPagination(newState);
  };
  const table = useAdvanceTable({
    data: rewardClaims,
    columns: tableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false,
    initialState: {
      columnVisibility: {}
    },
    meta: {
      manualPagination: true,
      totalRecords: totalRecords,
      onPaginationChange: handlePaginationChange,
      pagination: pagination,
      getList: getList
    }
  });

  return (
    <div>
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Daily Claim Records — Last 30 Days</h5>
        </Card.Header>
        <Card.Body>
          {chartLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: '320px' }}
            >
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <ClaimRecordsDailyChart data={dailyStats} />
          )}
        </Card.Body>
      </Card>

      <div className="mb-6">
        <AdvanceTableProvider {...table}>
          <Row className="g-3 justify-content-between mb-4">
            <Col xs="auto">
              <h2 className="mb-0">{totalRecords} Claim Records</h2>
            </Col>
            <Col xs="auto"></Col>
          </Row>
          <RewardClaimHistoryTable />
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default RewardClaimTransactionList;
